/**
 * Quick Add by SKU (/pages/quickadd).
 * Builds a full-catalog SKU → variant id map via paginated /products.json
 * because Liquid {% paginate %} can only expose one page (max 250) per render.
 */
(function () {
  'use strict';

  var PAGE_SIZE = 250;
  var MAX_PAGES = 40;
  var CACHE_KEY = 'clnf-sku-map-v1';
  var CACHE_TTL_MS = 10 * 60 * 1000;

  var skuMap = new Map();
  var catalogReady = false;
  var catalogLoad = null;

  function shopRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function debug() {
    if (window.__CLNF_DEBUG && window.console) {
      console.log.apply(console, arguments);
    }
  }

  function qs(sel, root) {
    return (root || document).querySelector(sel);
  }

  function setLoading(show, text) {
    var el = qs('.loadingdiv');
    if (!el) return;
    if (show) {
      el.textContent = text || 'Loading catalog…';
      el.style.display = 'block';
    } else {
      el.style.display = 'none';
    }
  }

  function clearInfo() {
    var el = qs('.iteminfo');
    if (el) el.textContent = '';
  }

  // Status lines are built with textContent so typed SKUs cannot inject HTML.
  function appendStatus(text, options) {
    var el = qs('.iteminfo');
    if (!el) return;
    var li = document.createElement('li');
    li.textContent = text;
    if (options && options.color) li.style.color = options.color;
    if (options && options.bold) li.style.fontWeight = 'bold';
    if (options && options.summary) {
      li.style.marginTop = '10px';
      li.style.borderTop = '1px solid #ccc';
      li.style.paddingTop = '5px';
    }
    el.appendChild(li);
  }

  function lookupSku(sku) {
    var trimmed = String(sku || '').trim();
    if (!trimmed) return null;
    return skuMap.get(trimmed) || skuMap.get(trimmed.toLowerCase()) || skuMap.get(trimmed.toUpperCase()) || null;
  }

  function addVariantToMap(variant) {
    if (!variant || variant.sku == null || variant.id == null) return;
    var sku = String(variant.sku).trim();
    if (!sku) return;
    var entry = { id: variant.id };
    skuMap.set(sku, entry);
    var lower = sku.toLowerCase();
    var upper = sku.toUpperCase();
    if (lower !== sku) skuMap.set(lower, entry);
    if (upper !== sku && upper !== lower) skuMap.set(upper, entry);
  }

  function addProductsToMap(products) {
    if (!products || !products.length) return;
    for (var i = 0; i < products.length; i++) {
      var variants = products[i].variants;
      if (!variants) continue;
      for (var j = 0; j < variants.length; j++) {
        addVariantToMap(variants[j]);
      }
    }
  }

  function readCache(expectedCount) {
    if (!expectedCount) return null;
    try {
      var parsed = JSON.parse(sessionStorage.getItem(CACHE_KEY));
      if (!parsed || parsed.count !== expectedCount || !Array.isArray(parsed.entries)) return null;
      if (Date.now() - parsed.ts > CACHE_TTL_MS) return null;
      return parsed.entries;
    } catch (e) {
      return null;
    }
  }

  function writeCache(expectedCount) {
    if (!expectedCount || !skuMap.size) return;
    try {
      var seen = {};
      var entries = [];
      skuMap.forEach(function (entry, sku) {
        if (!entry || seen[entry.id]) return;
        seen[entry.id] = true;
        entries.push([sku, entry.id]);
      });
      sessionStorage.setItem(
        CACHE_KEY,
        JSON.stringify({ count: expectedCount, ts: Date.now(), entries: entries })
      );
    } catch (e) {
      /* quota / private mode */
    }
  }

  function applyCacheEntries(entries) {
    for (var i = 0; i < entries.length; i++) {
      var pair = entries[i];
      if (!pair || pair.length < 2) continue;
      addVariantToMap({ sku: pair[0], id: pair[1] });
    }
  }

  function fetchPage(page) {
    return fetch(shopRoot() + 'products.json?limit=' + PAGE_SIZE + '&page=' + page, {
      credentials: 'same-origin',
      cache: 'no-store'
    }).then(function (res) {
      if (!res.ok) throw new Error('products.json ' + res.status);
      return res.json();
    }).then(function (data) {
      return (data && data.products) || [];
    });
  }

  function loadCatalog(expectedCount) {
    if (catalogReady && skuMap.size) return Promise.resolve();
    if (catalogLoad) return catalogLoad;

    catalogLoad = (function startLoad() {
      var cached = readCache(expectedCount);
      if (cached && cached.length) {
        skuMap.clear();
        applyCacheEntries(cached);
        catalogReady = true;
        debug('[sku-quick-add] cache hit', cached.length);
        return Promise.resolve();
      }

      skuMap.clear();

      // One page at a time. Firing every /products.json page in parallel (up to
      // MAX_PAGES) can 429 Shopify on a large catalog; sequential stays well
      // under the storefront rate limit and still covers the full catalog.
      function next(page) {
        setLoading(true, 'Loading catalog… (' + page + ')');
        return fetchPage(page).then(function (products) {
          addProductsToMap(products);
          if (products.length < PAGE_SIZE || page >= MAX_PAGES) {
            catalogReady = true;
            writeCache(expectedCount);
            debug('[sku-quick-add] mapped', skuMap.size, 'keys from', page, 'pages');
            return;
          }
          return next(page + 1);
        });
      }

      return next(1);
    })().then(
      function () {
        catalogLoad = null;
      },
      function (err) {
        catalogLoad = null;
        throw err;
      }
    );

    return catalogLoad;
  }

  function parseSkuEntry(raw) {
    var trimmed = String(raw || '').trim();
    if (!trimmed) return null;
    var dash = trimmed.lastIndexOf('-');
    if (dash > 0) {
      var qtyPart = trimmed.slice(dash + 1).trim();
      var qty = parseInt(qtyPart, 10);
      if (qtyPart !== '' && String(qty) === qtyPart && qty > 0) {
        return { sku: trimmed.slice(0, dash).trim(), quantity: qty };
      }
    }
    return { sku: trimmed, quantity: 1 };
  }

  function updateCartUI() {
    return fetch(shopRoot() + 'cart.js', { cache: 'no-store', credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('Cart fetch failed');
        return res.json();
      })
      .then(function (cart) {
        if (window.wetheme && typeof window.wetheme.updateCartDrawer === 'function') {
          try {
            window.wetheme.updateCartDrawer(cart);
          } catch (e) {
            debug('[sku-quick-add] wetheme drawer update failed', e);
          }
        }
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cart } }));
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { openDrawer: false, cart: cart } }));
        document.dispatchEvent(new CustomEvent('cart:build'));
      })
      .catch(function (error) {
        debug('[sku-quick-add] cart UI update failed', error);
      });
  }

  function addSkuToCart(sku, quantity) {
    var variant = lookupSku(sku);
    if (!variant) {
      return Promise.resolve({ success: false, message: 'SKU "' + sku + '" not found.' });
    }
    return fetch(shopRoot() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: variant.id, quantity: quantity })
    }).then(function (response) {
      return response.text().then(function (text) {
        var data = null;
        try {
          data = text ? JSON.parse(text) : null;
        } catch (e) {
          data = null;
        }
        if (!response.ok) {
          var desc = (data && (data.description || data.message)) || ('HTTP ' + response.status);
          return { success: false, message: 'Could not add SKU "' + sku + '": ' + desc };
        }
        return updateCartUI().then(function () {
          return { success: true, message: 'Added ' + quantity + 'x SKU "' + sku + '" to cart.' };
        });
      });
    }).catch(function () {
      return { success: false, message: 'Error adding SKU "' + sku + '" to cart.' };
    });
  }

  function processInput(input) {
    var entries = input.split(',').map(function (part) { return part.trim(); }).filter(Boolean);
    if (!entries.length) {
      clearInfo();
      appendStatus('Please enter at least one SKU.', { color: 'red' });
      return Promise.resolve(0);
    }

    clearInfo();
    var successCount = 0;
    var i = 0;

    function next() {
      if (i >= entries.length) {
        if (entries.length > 1) {
          var color = successCount === entries.length ? 'green' : (successCount > 0 ? 'orange' : 'red');
          appendStatus('Summary: ' + successCount + ' of ' + entries.length + ' items added.', {
            color: color,
            bold: true,
            summary: true
          });
        }
        return successCount;
      }
      var parsed = parseSkuEntry(entries[i]);
      i += 1;
      if (!parsed || !parsed.sku) {
        appendStatus('Invalid entry: "' + entries[i - 1] + '"', { color: 'red' });
        return next();
      }
      setLoading(true, 'Processing ' + i + ' of ' + entries.length + '…');
      return addSkuToCart(parsed.sku, parsed.quantity).then(function (result) {
        appendStatus(result.message, {
          color: result.success ? 'green' : 'red',
          bold: result.success
        });
        if (result.success) successCount += 1;
        return next();
      });
    }

    return next();
  }

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  onReady(function () {
    var root = qs('#clnf-sku-quick-add');
    if (!root) return;

    var input = qs('#querysku', root);
    var button = qs('.cartskubtn', root);
    var expectedCount = parseInt(root.getAttribute('data-product-count'), 10) || 0;
    var originalLabel = button ? button.textContent : 'Add';

    if (button) button.disabled = true;
    setLoading(true, 'Loading catalog…');

    loadCatalog(expectedCount)
      .then(function () {
        if (button) button.disabled = false;
        setLoading(false);
      })
      .catch(function (err) {
        debug('[sku-quick-add] catalog load failed', err);
        setLoading(false);
        clearInfo();
        appendStatus('Could not load the product catalog. Refresh and try again.', { color: 'red' });
        if (button) button.disabled = false;
      });

    function submit() {
      if (!input || !button) return;
      var value = input.value.trim();
      if (!value) {
        clearInfo();
        appendStatus('Please enter a SKU.', { color: 'red' });
        return;
      }
      if (!catalogReady) {
        setLoading(true, 'Loading catalog…');
        loadCatalog(expectedCount).then(function () {
          return processInput(value);
        }).then(finishSubmit, finishSubmit);
        return;
      }
      button.disabled = true;
      button.textContent = 'Adding...';
      processInput(value).then(finishSubmit, finishSubmit);

      function finishSubmit(successCount) {
        button.disabled = false;
        button.textContent = originalLabel;
        setLoading(false);
        if (successCount > 0) {
          window.setTimeout(function () {
            input.value = '';
          }, 2000);
        }
      }
    }

    if (input) {
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          submit();
        }
      });
      input.addEventListener('input', function () {
        var info = qs('.iteminfo');
        if (info && info.childNodes.length) info.textContent = '';
      });
    }
    if (button) {
      button.addEventListener('click', function (e) {
        e.preventDefault();
        submit();
      });
    }
  });
})();
